import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Placement } from '@floating-ui/react';
import { css, cx } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName, IconColorType } from '@frontend/icons';
import { classNames } from '@frontend/string';
import { theme } from '@frontend/theme';
import {
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  contextFactory,
  ActionButton,
  useEventListener,
  Button,
  ButtonSize,
  PopoverMenuItemDropdown,
} from '@frontend/design-system';

const adaptoActionsDivStyle1 = css`
  display: flex;
  gap: var(--adapto-actions-div-inline-expr0);
  max-width: 100%;
`;

const componentComponentStyle2 = css`
  color: var(--component--component-inline-color);
  & svg {
    color: var(--component--component-inline-icon-color);
  }
`;

const componentButtonStyle3 = css`
  color: var(--component--button-inline-color);
  & svg {
    color: var(--component--button-inline-icon-color);
  }
`;

const componentActionButtonStyle4 = css`
  color: var(--component--action-button-inline-color);
  & svg {
    color: var(--component--action-button-inline-color);
  }
`;

const componentButtonStyle5 = css`
  & svg {
    color: var(--component--button-inline-icon-color);
  }
`;

const componentActionButtonStyle6 = css`
  color: var(--component--action-button-inline-icon-color);
`;

interface ActionsContextValue {
  isInsidePopover: boolean;
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  index: number;
  handleClosePopover: () => void;
}

const smallButtonClass = css`
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const smallButtonStyles = (isSmall: boolean) => (isSmall ? smallButtonClass : undefined);

const ActionsContext = createContext<ActionsContextValue | undefined>(undefined);

export const useActionsContext = () => useContext(ActionsContext);

type AdaptoActionsVariant = 'icon-buttons' | 'action-buttons' | 'secondary-buttons';

interface AdaptoActionsProps {
  children: React.ReactNode;
  maxActions?: number;
  shouldOptimizeSpace?: boolean;
  spacing?: number;
  variant?: AdaptoActionsVariant;
  iconColor?: IconColorType | 'inherit';
  size?: ButtonSize;
  enableCloseEvent?: boolean;
  moreActionsTrackingId?: string;
  popoverPlacement?: Placement;
}

type AdaptoActionsContextValue = {
  variant: AdaptoActionsVariant;
  size?: ButtonSize;
  iconColor?: string;
};

const [Actions, useActions] = contextFactory<AdaptoActionsContextValue>();

const CLOSE_ADAPTO_ACTIONS = 'closeAdaptoActions';

export const AdaptoActions = ({
  children,
  maxActions = 4,
  shouldOptimizeSpace = false,
  spacing = 1,
  variant = 'icon-buttons',
  size = 'large',
  iconColor = 'primary',
  enableCloseEvent,
  moreActionsTrackingId,
  popoverPlacement,
  ...rest
}: AdaptoActionsProps) => {
  const [visibleActionsCount, setVisibleActionsCount] = useState(maxActions - 1);
  const containerRef = useRef<HTMLDivElement>(null);
  const actions = React.Children.toArray(children).filter(Boolean);
  const actionsCount = actions.length;
  const visibleActions = actions.slice(0, visibleActionsCount);
  const popoverActions = actions.slice(visibleActionsCount);

  const optimizeSpace = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const actionWidth = 40;
      const actionSpacing = spacing * 8;
      const maxVisibleActions = Math.floor((containerWidth + actionSpacing) / (actionWidth + actionSpacing)) - 1;
      const remainingActions = actions.length - maxVisibleActions;
      const visibleActionsCount = remainingActions === 1 ? actionsCount : maxVisibleActions;

      setVisibleActionsCount(visibleActionsCount);
    }
  };

  useEffect(() => {
    if (shouldOptimizeSpace) {
      optimizeSpace();

      const resizeObserver = new ResizeObserver(optimizeSpace);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
      };
    } else {
      if (actions.length === maxActions) {
        setVisibleActionsCount(maxActions);
        return;
      }

      setVisibleActionsCount(Math.min(maxActions - 1, actions.length));
    }

    return;
  }, [actions.length, maxActions, shouldOptimizeSpace]);

  const getIconColor = () => {
    if (iconColor === 'inherit' || variant === 'secondary-buttons') return undefined;

    return theme.font.colors[iconColor ?? 'default'];
  };
  const getIconSize = () => {
    if (variant === 'secondary-buttons' || variant === 'action-buttons') return undefined;

    return size;
  };
  const renderActions = () => {
    const calculatedColor = getIconColor();
    const calculatedSize = getIconSize();

    return (
      <Actions.Provider value={{ variant, size: calculatedSize, iconColor: calculatedColor }}>
        {visibleActions.map((action, index) => (
          <React.Fragment key={index}>{action}</React.Fragment>
        ))}
        {popoverActions.length > 0 && (
          <MoreActionPopover
            popoverPlacement={popoverPlacement}
            enableCloseEvent={enableCloseEvent}
            actions={popoverActions}
            trackingId={moreActionsTrackingId}
          />
        )}
      </Actions.Provider>
    );
  };

  return (
    <div
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
      {...rest}
      style={
        {
          '--adapto-actions-div-inline-expr0': theme.spacing(spacing),
        } as any
      }
      className={cx(adaptoActionsDivStyle1)}
    >
      {renderActions()}
    </div>
  );
};

interface ActionProps {
  icon: IconName;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disableCloseOnSelect?: boolean;
  subMenuItems?: Pick<React.ComponentProps<typeof PopoverMenuItemDropdown>, 'subMenuItems'>['subMenuItems'];
  trackingId?: string;
}

type Action = Omit<React.ComponentProps<typeof Button>, 'ref' | 'size' | 'onClick' | 'children' | 'icon'> & ActionProps;

type OnClickEvent = React.MouseEventHandler<HTMLButtonElement> & React.MouseEventHandler<HTMLAnchorElement>;

const getColor = ({ disabled, destructive, color }: Pick<Action, 'color' | 'disabled' | 'destructive'>) => {
  if (disabled) return theme.font.colors.disabled;
  if (destructive) return theme.font.colors.error;
  if (!!color) return color;
  return 'inherit';
};

const getIconColor = ({ disabled, destructive, color }: Pick<Action, 'color' | 'disabled' | 'destructive'>) => {
  if (disabled) return theme.font.colors.disabled;
  if (destructive) return theme.font.colors.error;
  return color;
};

export const Action = forwardRef<HTMLButtonElement, Action>(
  (
    {
      disableCloseOnSelect = false,
      label,
      hoverLabel,
      onClick,
      icon,
      showLabelWhenDisabled,
      className,
      subMenuItems,
      ...rest
    },
    ref
  ) => {
    const { isInsidePopover, getItemProps, index, handleClosePopover } = useActionsContext() ?? {};
    const { variant, size, iconColor: defaultIconColor } = useActions();

    const color = getColor({ disabled: rest.disabled, destructive: rest.destructive, color: rest.color || '' });

    const iconColor = getIconColor({
      disabled: rest.disabled,
      destructive: rest.destructive,
      color: rest.color ?? defaultIconColor,
    });

    if (isInsidePopover) {
      const Component = subMenuItems ? PopoverMenuItemDropdown : PopoverMenuItem;
      return (
        <Component
          Icon={() => <Icon name={icon} size={16} />}
          {...(getItemProps?.({
            onClick,
            disableCloseOnSelect,
            index: index ?? 0,
            className: classNames(className, 'action-item action-item--action'),
            hoverLabel,
            showLabelWhenDisabled,
            ...(rest as unknown as React.ComponentProps<typeof PopoverMenuItem>),
          }) ?? {})}
          {...(subMenuItems && {
            subMenuItems,
            showOnHover: true,
            closeParentMenu: handleClosePopover,
          })}
          className={componentComponentStyle2}
          style={
            {
              '--component--component-inline-color': color,
              '--component--component-inline-icon-color': iconColor,
            } as any
          }
          ref={ref}
        >
          {label}
        </Component>
      );
    }

    if (variant === 'icon-buttons') {
      return (
        <Button
          hoverLabel={hoverLabel || label}
          iconName={icon}
          size={size}
          variant='secondary'
          onClick={onClick as OnClickEvent}
          {...rest}
          className={cx(
            componentButtonStyle3,
            smallButtonStyles(size === 'small'),
            rest.disabled ? '' : classNames(className, 'action-item action-item--icon')
          )}
          style={
            {
              '--component--button-inline-color': color,
              '--component--button-inline-icon-color': iconColor,
            } as any
          }
          ref={ref}
        />
      );
    }

    if (variant === 'secondary-buttons') {
      return (
        <Button
          variant='secondary'
          hoverLabel={hoverLabel || label}
          onClick={onClick}
          className={classNames(className, 'action-item action-item--secondary')}
          {...rest}
          ref={ref}
        >
          <Icon name={icon} size={16} />
        </Button>
      );
    }

    return (
      <ActionButton
        ref={ref}
        label={hoverLabel || label}
        onClick={onClick}
        {...rest}
        className={cx(componentActionButtonStyle4, classNames(className, 'action-item action-item--button'))}
        style={
          {
            '--component--action-button-inline-color': color,
          } as any
        }
      >
        <Icon name={icon} size={16} />
      </ActionButton>
    );
  }
);

Action.displayName = 'Action';

export const MoreAction = forwardRef<HTMLButtonElement, Omit<ActionProps, 'label'>>(({ onClick, ...rest }, ref) => {
  const { t } = useTranslation();
  const { variant, size, iconColor } = useActions();

  if (variant === 'icon-buttons' || variant === 'secondary-buttons') {
    return (
      <Button
        size={size}
        iconName='more'
        hoverLabel={t('More')}
        variant='secondary'
        onClick={onClick as OnClickEvent}
        aria-label={t('Action menu')}
        {...rest}
        style={
          {
            '--component--button-inline-icon-color': iconColor,
          } as any
        }
        className={cx(componentButtonStyle5, smallButtonStyles(size === 'small'))}
        ref={ref}
      />
    );
  }

  return (
    <ActionButton
      aria-label={t('Action menu')}
      label='More'
      onClick={onClick as OnClickEvent}
      {...rest}
      style={
        {
          '--component--action-button-inline-icon-color': iconColor,
        } as any
      }
      className={cx(componentActionButtonStyle6)}
      ref={ref}
    >
      <Icon name='more' size={16} />
    </ActionButton>
  );
});

MoreAction.displayName = 'MoreAction';

interface MoreActionPopoverProps {
  actions: React.ReactNode[];
  enableCloseEvent?: boolean;
  trackingId?: string;
  popoverPlacement?: Placement;
}

const MoreActionPopover: React.FC<MoreActionPopoverProps> = ({
  actions,
  enableCloseEvent = false,
  trackingId,
  popoverPlacement,
}) => {
  const { getMenuProps, getTriggerProps, getItemProps, close } = usePopoverMenu({
    placement: popoverPlacement ?? 'bottom-end',
    omitSizeMiddleware: true,
  });

  const handleClosePopover = useCallback(() => {
    close();
  }, [close]);

  useEventListener(CLOSE_ADAPTO_ACTIONS as any, handleClosePopover, enableCloseEvent);

  return (
    <>
      <MoreAction {...getTriggerProps()} trackingId={trackingId} />
      <PopoverMenu {...getMenuProps()} alwaysRender>
        {actions.map((action, index) => (
          <ActionsContext.Provider
            key={index}
            value={{ isInsidePopover: true, getItemProps, index, handleClosePopover }}
          >
            {action}
          </ActionsContext.Provider>
        ))}
      </PopoverMenu>
    </>
  );
};

const closeAdaptoActions = new CustomEvent(CLOSE_ADAPTO_ACTIONS);

export const closeAdaptoActionsPopover = () => {
  window.dispatchEvent(closeAdaptoActions);
};

AdaptoActions.Action = Action;

/**
 * This function will be available only if `enableCloseEvent` props is passed to AdaptoActions wrapper.
 */
AdaptoActions.close = closeAdaptoActionsPopover;
